import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TablePagination,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, AttachFile as AttachFileIcon, Search as SearchIcon } from '@mui/icons-material';
import api from '../../services/api';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';
import productoService from '../../services/productoService';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Divider from '@mui/material/Divider';

interface Producto {
  codigoArticulo: string;
  descripcion: string;
  nombreLargo?: string;
  codBarra?: string;
  codigoProveedor?: string;
  precioVenta?: number;
  cantidad?: number;
  stockMin?: number;
  stockMax?: number;
  costo?: number;
  codigoRamo?: string;
  codigoLinea?: string;
  codigoClase?: string;
  unidadMedida?: {
    idUnidadMedida: string;
    descripcion: string;
  };
}

interface ProductoPagedResult {
  items: Producto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

const Productos: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [productosData, setProductosData] = useState<ProductoPagedResult>({
    items: [],
    totalCount: 0,
    pageNumber: 1,
    pageSize: 10,
    totalPages: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [openImportar, setOpenImportar] = useState(false);
  const columnasAPI = [
    'codigoArticulo',
    'nombreLargo',
    'codBarra',
    'descripcion',
    'costo',
    'precioVenta',
    'estado',
    'codigoRamo',
    'codigoLinea',
    'codigoClase',
    'idUnidadMedida',
    'cantidad',
    'stockMin',
    'stockMax',
    'tipoArticulo',
    'calculaIva',
    'ivaRT',
    'ivaRN',
    'ivaCF',
    'ivaEX',
    'ivaMT',
    'impInterno1',
    'impInterno2',
    'tasaHidrica',
    'porc_Desc',
    'codigoProveedor',
    'codigoArticuloProveedor'
  ];
  const camposObligatorios = [
    'codigoArticulo','descripcion','codigoRamo','codigoLinea','codigoClase'
  ];
  const camposNumericos = [
    'costo','precioVenta','cantidad','stockMin','stockMax','ivaRT','ivaRN','ivaCF','ivaEX','ivaMT','impInterno1','impInterno2','tasaHidrica','porc_Desc'
  ];

  const fetchProductos = async (pageNumber: number, pageSize: number, search?: string) => {
    try {
      const response = await api.get('/api/productos', {
        params: {
          pageNumber: pageNumber + 1,
          pageSize,
          searchTerm: search
        }
      });
      
      console.log('Productos disponibles:', response.data.items.map((item: Producto) => ({
        codigoArticulo: item.codigoArticulo,
        descripcion: item.descripcion,
        codigoRamo: item.codigoRamo,
        codigoLinea: item.codigoLinea,
        codigoClase: item.codigoClase,
        precioVenta: item.precioVenta
      })));
      
      const adjustedData = {
        ...response.data,
        pageNumber: response.data.pageNumber - 1
      };
      
      setProductosData(adjustedData);
    } catch (error) {
      console.error('Error al cargar productos:', error);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchProductos(page, rowsPerPage, searchTerm);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleDelete = async (codigo: string) => {
    if (window.confirm('¿Está seguro de eliminar este producto?')) {
      try {
        await api.delete(`/api/productos/${codigo}`);
        fetchProductos(page, rowsPerPage, searchTerm);
      } catch (error) {
        console.error('Error al eliminar producto:', error);
      }
    }
  };

  // Plantilla Excel fiel exactamente al modelo de la API:
  const handleDescargarPlantilla = () => {
    // Lista exacta según tu API:
    const columnasAPI = [
      'codigoArticulo',
      'nombreLargo',
      'codBarra',
      'descripcion',
      'costo',
      'precioVenta',
      'estado',
      'codigoRamo',
      'codigoLinea',
      'codigoClase',
      'idUnidadMedida',
      'cantidad',
      'stockMin',
      'stockMax',
      'tipoArticulo',
      'calculaIva',
      'ivaRT',
      'ivaRN',
      'ivaCF',
      'ivaEX',
      'ivaMT',
      'impInterno1',
      'impInterno2',
      'tasaHidrica',
      'porc_Desc',
      'codigoProveedor',
      'codigoArticuloProveedor'
    ];
    try {
      const data = [columnasAPI, new Array(columnasAPI.length).fill('')];
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Productos');
      const instrucciones = [
        ['Columna', 'Descripción', 'Valores aceptados'],
        ['codigoArticulo', 'Código único del artículo', 'Texto alfanumérico (obligatorio)'],
        ['nombreLargo', 'Nombre largo', 'Texto (opcional)'],
        ['codBarra', 'Código de barras', 'Texto (opcional)'],
        ['descripcion', 'Descripción', 'Texto (obligatorio)'],
        ['costo', 'Costo', 'Número (opcional)'],
        ['precioVenta', 'Precio de venta', 'Número (opcional)'],
        ['estado', 'Estado del artículo', 'Texto (opcional)'],
        ['codigoRamo', 'Ramo', 'Código ramo existente (obligatorio)'],
        ['codigoLinea', 'Línea', 'Código línea existente (obligatorio)'],
        ['codigoClase', 'Clase', 'Código clase existente (obligatorio)'],
        ['idUnidadMedida', 'Unidad de medida', 'ID de unidad existente (opcional)'],
        ['cantidad', 'Cantidad', 'Número (opcional)'],
        ['stockMin', 'Stock mínimo', 'Número (opcional)'],
        ['stockMax', 'Stock máximo', 'Número (opcional)'],
        ['tipoArticulo', 'Tipo de artículo', 'Texto (opcional)'],
        ['calculaIva', 'Calcula IVA', 'Texto (opcional)'],
        ['ivaRT', 'IVA Retención', 'Número (opcional)'],
        ['ivaRN', 'IVA RN', 'Número (opcional)'],
        ['ivaCF', 'IVA CF', 'Número (opcional)'],
        ['ivaEX', 'IVA Exento', 'Número (opcional)'],
        ['ivaMT', 'IVA Monotributo', 'Número (opcional)'],
        ['impInterno1', 'Impuesto interno 1', 'Número (opcional)'],
        ['impInterno2', 'Impuesto interno 2', 'Número (opcional)'],
        ['tasaHidrica', 'Tasa hídrica', 'Número (opcional)'],
        ['porc_Desc', 'Porcentaje descuento', 'Número (opcional)'],
        ['codigoProveedor', 'Proveedor', 'Código proveedor existente (opcional)'],
        ['codigoArticuloProveedor', 'Código artículo proveedor', 'Texto (opcional)'],
      ];
      const wsInstrucciones = XLSX.utils.aoa_to_sheet(instrucciones);
      XLSX.utils.book_append_sheet(wb, wsInstrucciones, 'Instrucciones');
      const fileName = 'PlantillaProductos.xlsx';
      XLSX.writeFile(wb, fileName);
      enqueueSnackbar('✅ Plantilla Excel descargada correctamente', {
        variant: 'success', autoHideDuration: 4000
      });
    } catch (error) {
      enqueueSnackbar('❌ Error al crear la plantilla Excel', {
        variant: 'error', autoHideDuration: 4000
      });
    }
  };

  const onImportChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: '', header: 0 });
      enqueueSnackbar('Archivo leído, procesando filas.', { variant: 'info', autoHideDuration: 3000 });
      if (!rows.length) {
        enqueueSnackbar('El archivo está vacío.', { variant: 'warning', autoHideDuration: 4000 });
        return;
      }
      // Normalizar cabeceras conocidas (alias) antes de validar
      const headerAliases: Record<string, string> = { codProveedor: 'codigoProveedor' };
      const normalizedRows = rows.map((r) => {
        const copy: any = { ...r };
        Object.entries(headerAliases).forEach(([from, to]) => {
          if (copy[from] !== undefined && copy[to] === undefined) {
            copy[to] = copy[from];
          }
        });
        return copy;
      });
      const headers = Object.keys(normalizedRows[0]);
      const noEncontrados = columnasAPI.filter(h => !headers.includes(h));
      if (noEncontrados.length > 0) {
        enqueueSnackbar('Columnas faltantes: ' + noEncontrados.join(', '), { variant: 'error', autoHideDuration: 5000 });
        return;
      }
      // 1. Pre-carga lista de códigos ya existentes para control de duplicados (cargar en lotes de 20)
      const pageSizeDup = 20; // Límite del backend
      const productosResponse = await productoService.getProductos({ pageNumber: 1, pageSize: pageSizeDup });
      const existeCodigo = new Set((productosResponse.items || []).map((p: any) => String(p.codigoArticulo)));
      let exitos = 0, fallos = 0;
      for (let i = 0; i < normalizedRows.length; i++) {
        const row = normalizedRows[i];
        // 1. Validar obligatorios
        let faltanCampos: string[] = [];
        for(const campo of camposObligatorios) {
          if (!row[campo]) faltanCampos.push(campo);
        }
        if (faltanCampos.length > 0) {
          enqueueSnackbar(`❌ Fila ${i + 2}: Falta completar: ${faltanCampos.join(', ')}`, { variant: 'error', autoHideDuration: 8000 });
          fallos++; continue;
        }
        // 2. Validar duplicado
        if (existeCodigo.has(String(row.codigoArticulo))) {
          enqueueSnackbar(`🔴 Producto duplicado (codigoArticulo: ${row.codigoArticulo})`, { variant: 'error', autoHideDuration: 8000 });
          fallos++; continue;
        }
        // 3. Validación de tipo correcto
        let camposTipoMal: string[] = [];
        for (const nCampo of camposNumericos) {
          if (row[nCampo] !== '' && row[nCampo] !== undefined && row[nCampo] !== null && isNaN(Number(row[nCampo]))) {
            camposTipoMal.push(nCampo);
          }
        }
        if (camposTipoMal.length > 0) {
          enqueueSnackbar(`❌ Fila ${i + 2}: campo inválido: ${camposTipoMal.join(', ')} (debe ser número)`, { variant: 'error', autoHideDuration: 8000 });
          fallos++; continue;
        }
        // 4. Mapear datos para API: claves SIEMPRE como string (vacío si no hay dato), números como number/null
        const producto: any = {};
        columnasAPI.forEach((key) => {
          if (camposNumericos.includes(key)) {
            producto[key] = (row[key] !== '' && row[key] !== undefined && row[key] !== null) ? Number(row[key]) : null;
          } else if (key === 'idUnidadMedida') {
            // Enviar null si está vacío para evitar 400 por tipo inválido
            producto[key] = (row[key] !== '' && row[key] !== undefined && row[key] !== null) ? String(row[key]) : null;
          } else if (
            key.startsWith('codigo') ||
            key.startsWith('id') ||
            key === 'estado' ||
            key === 'tipoArticulo' ||
            typeof row[key] === 'string'
          ) {
            producto[key] = (row[key] !== '' && row[key] !== undefined && row[key] !== null) ? String(row[key]) : '';
          } else {
            producto[key] = (row[key] !== '' && row[key] !== undefined) ? row[key] : null;
          }
        });
        console.log('Fila', i+2, '- DATOS enviados:', producto);
        try {
          await productoService.createProducto(producto);
          exitos++;
        } catch (err: any) {
          // Mostrar mayor detalle del error 400 si viene del backend
          let mensajePlano = err?.response?.data?.message || err?.response?.data || err?.message || 'Error en backend';
          if (err?.response?.data?.errors) {
            const errores = err.response.data.errors;
            let detalle = '';
            for (let campo in errores) {
              if (Array.isArray(errores[campo])) {
                detalle += `${campo}: ${errores[campo].join('; ')}\n`;
              }
            }
            enqueueSnackbar('❌ Error en los datos: ' + detalle, { variant: 'error', autoHideDuration: 12000 });
            fallos++;
            continue;
          }
          if (typeof mensajePlano !== 'string') {
            try { mensajePlano = JSON.stringify(mensajePlano); } catch {}
          }
          console.log('Fila', i+2, '- ERROR API:', mensajePlano);
          enqueueSnackbar(`❌ Fila ${i+2}: Error al guardar: ${String(mensajePlano).slice(0,120)}`,
            { variant: 'error', autoHideDuration: 9000 });
          fallos++;
        }
      }
      enqueueSnackbar(`Importación completada. Éxitos: ${exitos}, Fallos: ${fallos}`, { variant: fallos > 0 ? 'error' : 'success', autoHideDuration: 4500 });
      fetchProductos(page, rowsPerPage, searchTerm);
      setTimeout(() => { setOpenImportar(false); }, 800);
    } catch (error) {
      enqueueSnackbar('Error leyendo el archivo Excel (¿archivo corrupto/formato incorrecto?)', { variant: 'error', autoHideDuration: 9000 });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header con controles */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600,
                color: '#1976d2'
              }}
            >
              Productos
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate('/inventario/productos/nuevo')}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                {isMobile ? 'Nuevo' : 'Nuevo Producto'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<AttachFileIcon />}
                onClick={() => setOpenImportar(true)}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                {isMobile ? 'Importar' : 'Importar productos'}
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mb: 3 }}>
            <TextField
              label="Buscar"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fafafa',
                  '&:hover': {
                    bgcolor: '#f5f5f5',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#ffffff',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#666' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Tabla o Cards según el tamaño de pantalla */}
          {isMobile ? (
            <Stack spacing={2}>
              {productosData.items.map((producto) => (
                <Card 
                  key={producto.codigoArticulo}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {producto.descripcion}
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Código:</strong> {producto.codigoArticulo}
                      </Typography>
                      {producto.nombreLargo && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Nombre Largo:</strong> {producto.nombreLargo}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {producto.codigoRamo && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Ramo:</strong> {producto.codigoRamo}
                          </Typography>
                        )}
                        {producto.codigoLinea && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Línea:</strong> {producto.codigoLinea}
                          </Typography>
                        )}
                        {producto.codigoClase && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>Clase:</strong> {producto.codigoClase}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Precio:</strong> ${producto.precioVenta?.toFixed(2) || '0.00'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: (producto.cantidad || 0) > 0 ? 'success.main' : 'error.main' }}>
                          <strong>Stock:</strong> {producto.cantidad || 0}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Tooltip title="Editar producto">
                          <IconButton
                            onClick={() => navigate(`/inventario/productos/${encodeURIComponent(producto.codigoArticulo)}`)}
                            size="small"
                            sx={{
                              bgcolor: '#2196f3',
                              color: '#fff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#1976d2' }
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar producto">
                          <IconButton
                            onClick={() => handleDelete(producto.codigoArticulo)}
                            size="small"
                            sx={{
                              bgcolor: '#f44336',
                              color: '#fff',
                              borderRadius: 1.5,
                              '&:hover': { bgcolor: '#d32f2f' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              {productosData.items.length === 0 && (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  No hay productos para mostrar
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={productosData.totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
              />
            </Stack>
          ) : (
            <TableContainer 
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nombre Largo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ramo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Línea</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Clase</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Precio</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Stock</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productosData.items.map((producto) => (
                    <TableRow 
                      key={producto.codigoArticulo}
                      sx={{
                        '&:hover': {
                          bgcolor: '#f9f9f9',
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{producto.codigoArticulo}</TableCell>
                      <TableCell>{producto.descripcion}</TableCell>
                      <TableCell>{producto.nombreLargo}</TableCell>
                      <TableCell>{producto.codigoRamo}</TableCell>
                      <TableCell>{producto.codigoLinea}</TableCell>
                      <TableCell>{producto.codigoClase}</TableCell>
                      <TableCell align="right">${producto.precioVenta?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="right">{producto.cantidad || 0}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Editar producto">
                            <IconButton
                              onClick={() => navigate(`/inventario/productos/${encodeURIComponent(producto.codigoArticulo)}`)}
                              size="small"
                              sx={{
                                bgcolor: '#2196f3',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { 
                                  bgcolor: '#1976d2',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar producto">
                            <IconButton
                              onClick={() => handleDelete(producto.codigoArticulo)}
                              size="small"
                              sx={{
                                bgcolor: '#f44336',
                                color: '#fff',
                                borderRadius: 1.5,
                                width: 32,
                                height: 32,
                                '&:hover': { 
                                  bgcolor: '#d32f2f',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s'
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 20]}
                component="div"
                count={productosData.totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                sx={{
                  borderTop: '1px solid #e0e0e0',
                }}
              />
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Modal de importación */}
      <Dialog open={openImportar} onClose={() => setOpenImportar(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap:1 }}>
            <CloudUploadIcon sx={{ color: 'primary.main', fontSize: 30, mr: 1 }} />
            <Typography variant="h6" fontWeight={700}>Importar Productos</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box>
            {/* Bloque 1: Descargar plantilla */}
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, mb: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudDownloadIcon color="primary" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>1. Descargar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Descarga la plantilla Excel con el formato correcto para importar productos.
              </Typography>
              <Button variant="contained" color="info" fullWidth sx={{ fontWeight: 700, py: 1.2, mb: 1 }} startIcon={<CloudDownloadIcon />} onClick={handleDescargarPlantilla}>
                Descargar Plantilla
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            {/* Bloque 2: Importar desde archivo */}
            <Box sx={{ border: '2px dashed #ccc', borderRadius: 3, p: 3, background: '#fafbfd' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb:1 }}>
                <CloudUploadIcon color="success" sx={{ fontSize: 30, mr: 1 }} />
                <Typography component="span" fontWeight={700} fontSize={18}>2. Importar Plantilla</Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Selecciona tu archivo Excel completado para importar los productos.
              </Typography>
              <label htmlFor="excel-upload-productos">
                <input
                  id="excel-upload-productos"
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={onImportChange} // mantiene la lógica existente
                />
                <Button variant="contained" fullWidth color="success" startIcon={<CloudUploadIcon />} component="span" sx={{ fontWeight: 700, py: 1.2 }}>
                  Seleccionar Archivo
                </Button>
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImportar(false)} color="inherit">Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Productos;